import { validateLink } from '@w/md/src/parser/utils/link'
import { tableShape } from './table-grid'
export { tableShape } from './table-grid'
import { tableGrid } from '@w/content-blocks/src/table-html'
import type { Block, Blocks } from '@w/content-blocks/src/block'
import type { CoreState } from './state'
import { formatAt } from './selection'
import { children, locate, plain, pointAt, rows, sliceInline, type Row } from './tree-read'
import { replaceChildren, setBlockProps, unwrapBlock } from './ops'
import { after, insertAt } from './placement'

/**
 * The insert commands (table, divider, block formula) place their block through the ownership contract (placement.ts):
 * the row splits at the caret inside its own container. The caret lands in a table's first cell, else after the block.
 * Callers refuse inline-only slots (a table cell, a field).
 */
export function insertRichBlock(state: CoreState, row: Row, block: Block, offset: number): void {
  const { home, index } = insertAt(state, row, offset, block)
  const point = block.type === 'table' ? pointAt(rows([block])[0], 0) : after(home, index)
  state.selection = { anchor: point, focus: point }
}

export function createTable(rowCount: number, columns: number): Block {
  if (!Number.isInteger(rowCount) || !Number.isInteger(columns) || rowCount < 1 || columns < 1) {
    throw new Error('Table dimensions must be positive integers')
  }
  return { id: '', type: 'table', children: Array.from({ length: rowCount }, () => ({
    id: '', type: 'tr', children: Array.from({ length: columns }, () => ({
      id: '', type: 'td', children: [],
    })),
  })) }
}

export { editTable } from './table-edit'

/** A scheme-less URL links over https (Lexical `sanitizeUrl` callers prepend it the same way). */
export function linkHref(url: string): string {
  return /^[a-z][a-z\d+.-]*:/iu.test(url) ? url : 'https://' + url
}

/** The href a link may carry, or null when the markdown parser's `validateLink` refuses it (`javascript:`, `data:`…). */
export function safeLinkHref(url: string): string | null {
  const href = linkHref(url)
  return validateLink(href) ? href : null
}

export function linkRange(state: CoreState, entries: Row[],
  start: { row: number; offset: number }, end: { row: number; offset: number },
  url: string | null, reversed: boolean): void {
  if (url) {
    const href = safeLinkHref(url)
    if (href === null) return
    url = href
  }
  // Links go at any depth; inline wrappers (color, language…) stay around their text.
  const unwrap = (items: Blocks): Blocks => items.flatMap((block): Blocks => block.type === 'a'
    ? unwrap(block.children) : children(block).length > 0 ? [{ ...block, children: unwrap(children(block)) } as Block] : [block])
  // Lexical $toggleLink wraps text nodes: a new link goes inside the wrappers it covers, each run of leaves one link.
  const link = (items: Blocks, href: string): Blocks => {
    const linked: Blocks = []
    let run: Blocks = []
    const flush = () => { if (run.length > 0) linked.push({ id: '', type: 'a', href, children: run }); run = [] }
    for (const block of items) {
      if (children(block).length > 0) {
        flush()
        linked.push({ ...block, children: link(children(block), href) } as Block)
      } else run.push(block)
    }
    flush()
    return linked
  }
  const collapsed = start.row === end.row && start.offset === end.offset
  // A caret edits the link the toolbar shows for it, at any inline wrapper depth.
  const caretLink = collapsed ? selectedLinkBlock(state) : null
  if (caretLink?.type === 'a') {
    if (url) setBlockProps(state, caretLink, { href: url })
    else unwrapBlock(state, caretLink)
  }
  for (let index = start.row; index <= (collapsed ? -1 : end.row); index++) {
    const row = entries[index]
    const from = index === start.row ? start.offset : 0
    const to = index === end.row ? end.offset : plain(row.block).length
    const selected = unwrap(sliceInline(row.block.children, from, to, from > 0, state))
    replaceChildren(state, row.block, [...sliceInline(row.block.children, 0, from, false, state),
      ...(url ? link(selected, url) : selected),
      ...sliceInline(row.block.children, to, Infinity, true, state)])
  }
  const left = pointAt(entries[start.row], start.offset)
  const right = pointAt(entries[end.row], end.offset)
  state.selection = { anchor: reversed ? right : left, focus: reversed ? left : right }
}

/**
 * The href of the link at the selection's anchor, else at its focus (Lexical `getSelectedLinkNode`); null without one,
 * or for a node or table selection. A range edge touches a link only from inside it; a caret also at its boundaries.
 */
export function selectedLink(state: CoreState): string | null {
  const link = selectedLinkBlock(state)
  return link?.type === 'a' ? link.href : null
}

/** The existing link an edit targets (Lexical: the anchor's link, else the focus's): its id, for editing by identity. */
export function selectedLinkId(state: CoreState): string | null {
  return selectedLinkBlock(state)?.id ?? null
}

function selectedLinkBlock(state: CoreState): Block | null {
  const selection = state.selection
  if (!selection || selection.nodes?.length || selection.table) return null
  const entries = rows(state.blocks)
  const anchor = locate(entries, selection.anchor)
  const focus = locate(entries, selection.focus)
  const order = anchor.row - focus.row || anchor.offset - focus.offset
  const linkAt = ({ row, offset }: { row: number; offset: number }, edge: number): Block | null => {
    const items = entries[row] ? inlineLeaves(entries[row].block.children) : []
    for (const [index, { block, start, end }] of items.entries()) {
      if (block.type === 'a') {
        if (start < offset && offset < end) return block
        // Typing never enters a link at its edges (insertChars), so a caret there is the neighbouring text's, as Lexical
        // normalizes it off LinkNode edges; between two links, or with nothing beside, it stays on the link before.
        if (edge <= 0 && offset === start && start < end && (edge < 0 || index === 0)) return block
        if (edge >= 0 && offset === end && start < end && (edge > 0 || items[index + 1] === undefined || items[index + 1].block.type === 'a')) return block
      }
    }
    return null
  }
  // Edge sign: negative for the range start, positive for its end, zero for a caret.
  return linkAt(anchor, Math.sign(order)) ?? linkAt(focus, -Math.sign(order))
}

/** Every link a range overlaps, or the link holding its caret: the links a preview link just created. */
export function selectedLinkIds(state: CoreState): string[] {
  const selection = state.selection
  if (!selection || selection.nodes?.length || selection.table) return []
  const entries = rows(state.blocks)
  const [from, to] = [locate(entries, selection.anchor), locate(entries, selection.focus)]
    .toSorted((left, right) => left.row - right.row || left.offset - right.offset)
  const caret = from.row === to.row && from.offset === to.offset
  const ids: string[] = []
  for (let row = from.row; row <= to.row; row++) {
    const low = row === from.row ? from.offset : 0
    const high = row === to.row ? to.offset : Infinity
    for (const { block, start, end } of entries[row] ? inlineLeaves(entries[row].block.children) : []) {
      if (block.type === 'a' && (caret ? start <= low && low <= end : start < high && end > low)) ids.push(block.id)
    }
  }
  return ids
}

/** A row's inline content in offset order, links whole and inline wrappers (color, language…) opened: a link's owner is
 * its nearest link ancestor at any wrapper depth (Lexical `$findMatchingParent(node, $isLinkNode)`). */
/** The marks a character typed or plain text pasted at `offset` takes: the pending caret format, else the format at the
 * caret; none right after a link (one insertion format for typing and plain paste). */
export function insertionFormat(state: CoreState, row: Row, offset: number): number {
  if (inlineLeaves(row.block.children).some(({ block, end }) => block.type === 'a' && end === offset)) return 0
  return state.pendingFormat ?? formatAt(row, offset)
}

export function inlineLeaves(items: Blocks, from = 0): { block: Block; start: number; end: number }[] {
  const leaves: { block: Block; start: number; end: number }[] = []
  let start = from
  for (const block of items) {
    const end = start + plain(block).length
    if (block.type !== 'a' && children(block).length > 0) leaves.push(...inlineLeaves(children(block), start))
    else leaves.push({ block, start, end })
    start = end
  }
  return leaves
}

export function selectedTableRows(state: CoreState): Row[] {
  const table = state.selection?.table
  if (!table) return []
  const source = state.blocks.find((block) => block.id === table.tableId)
  if (source?.type !== 'table') return []
  const grid = tableGrid(source)
  const { from, to } = tableShape(grid, table)
  const ids = new Set<string>()
  for (let y = from.y; y <= to.y; y++) {
    for (let x = from.x; x <= to.x; x++) {
      const cell = grid[y]?.[x]
      if (cell) ids.add(cell.id)
    }
  }
  return rows(state.blocks).filter((entry) => ids.has(entry.block.id))
}
